/**
 * Linear — the third reference semantic connector profile.
 *
 * Two dialects proved the abstraction (Miro canvas, Notion tree); Linear is
 * the confirmation that a third — an issue tracker with typed issue relations
 * — needs nothing new either. Linear's `IssueRelation` (blocks / duplicate /
 * related) is its connector, its parent/sub-issue tree is its containment,
 * and its labels/workflow-state colors are its visual language.
 *
 * The gap this closes: the flat Linear import (linearService.importIssues)
 * reads issues but not their relations or parent, so blocking/sub-issue
 * structure never becomes canonical edges.
 */

import type { ConnectorProfile } from '../profileTypes';
import type { RelationshipCueRule, TypeMapRule } from '../../fieldMappingRegistry';

export const LINEAR_PROFILE: ConnectorProfile = {
  connectorType: 'linear',
  profileVersion: 1,

  nativeObjectTypes: ['issue', 'sub_issue', 'relation', 'label', 'state', 'project'],
  relationshipModel: 'references',
  visualChannels: ['fill_color'], // label / workflow-state color
  spatialModel: 'tree',
  acquisition: 'api',

  fidelity: {
    content: {
      export: 'full',
      import: 'full',
      notes: 'Issue title + description import fully.',
    },
    structure: {
      export: 'partial',
      import: 'partial',
      notes: 'Team/project membership → Collection; parent/sub-issue tree → `contains` edges. Board columns (workflow states) are not structure here — they map to category/lifecycle.',
    },
    relationships: {
      export: 'none',
      import: 'partial',
      notes: 'IssueRelation types map to canonical edges (blocks → dependency, related → related, duplicate → cluster). Parent/sub-issue → aggregation/breakdown. Export writes issues, not relations.',
    },
    semantics: {
      export: 'partial',
      import: 'partial',
      notes: 'The first colored label (or the workflow-state color) maps to item category via the workspace visual rules — same machinery as Miro fill colors and Notion option colors.',
    },
  },

  identity: { stableExternalIds: true, deepLinks: true, appMetadata: false },
  continuity: { webhooks: true, comments: false },
};

// ── Profile-shipped default maps (workspace v3 rules override) ────────────────

export const LINEAR_DEFAULT_TYPE_MAP: TypeMapRule[] = [
  { nativeType: 'issue', target: 'item' },
  { nativeType: 'sub_issue', target: 'item' },
  { nativeType: 'project', target: 'region' },
];

/**
 * Linear IssueRelation.type is a fixed vocabulary — 'blocks', 'related',
 * 'duplicate'. It is the cue (kind 'link_type'), resolved through the same
 * precedence as Miro captions and Notion property names. 'blocks' is directed
 * (source blocks target). Parent/sub-issue synthesize their own cues below.
 */
export const LINEAR_DEFAULT_RELATIONSHIP_MAP: RelationshipCueRule[] = [
  { cue: { kind: 'link_type', value: 'blocks' }, canonicalType: 'dependency', subtype: 'hard', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'link_type', value: 'blocked_by' }, canonicalType: 'prerequisite', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'link_type', value: 'related' }, canonicalType: 'related', directionality: 'undirected', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'link_type', value: 'duplicate' }, canonicalType: 'related', subtype: undefined, directionality: 'undirected', defaultConfidence: 0.9, basis: 'explicit' },
  // Parent / sub-issue tree
  { cue: { kind: 'link_type', value: 'parent' }, canonicalType: 'aggregation', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'link_type', value: 'sub_issue' }, canonicalType: 'breakdown', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'containment', value: 'parent' }, canonicalType: 'contains', directionality: 'directed', defaultConfidence: 0.85, basis: 'visual_language' },
];

/**
 * An unmatched relation type still becomes an edge — a generic `related`
 * proposal — rather than being dropped.
 */
export const LINEAR_UNNAMED_RELATION = {
  canonicalType: 'related' as const,
  directionality: 'undirected' as const,
  defaultConfidence: 0.6,
  basis: 'spatial' as const,
};
